import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';

/**
 * Custom hook for Decision Points Table (DPT) auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration
 * - Polling for data changes every 500ms
 * - Manual update flag to prevent polling overwrites
 * - Pending deletes tracking
 * - Selected month tracking for auto-save
 * 
 */
export const useDPTAutoSave = (
    isDPTEditing: boolean,
    decisionPointsData: any[],
    DPTRef: React.RefObject<any>,
    selectedMonth: string | null,
    pendingDeletes: any[],
    setPendingDeletes: (deletes: any[]) => void,
    setDecisionPointsData: (data: any[]) => void
) => {
    // Auto-save state
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);
    const [decisionPointsEditData, setDecisionPointsEditData] = useState<any[]>([]);
    const isManualUpdateRef = useRef(false);

    // Auto-save: construct unique pageKey for Decision Points
    const decisionPointsPageKey = useMemo(() => {
        return EDIT_PAGE_KEY_MAP.publishing_overview_decision_points;
    }, []);

    // Auto-save: get methods (check if auto-save exists) - First call without dependencies
    const autoSaveMethods: any = useAutoSaveParams({
        pageKey: decisionPointsPageKey,
    });

    const {
        getSaveParamsValue,
        showRestorePrompt,
        deleteSaveParamsByPageKey
    } = autoSaveMethods;

    // Track edit data changes for auto-save - Poll frequently to capture real-time edits
    useEffect(() => {
        if (isDPTEditing && DPTRef.current) {
            // Poll getEditData() frequently to capture latest edits (since TableContent doesn't expose onEditDataChange)
            // We poll every 500ms to ensure we capture changes before the 5-second auto-save
            const interval = setInterval(() => {
                // Skip polling if we just did a manual update (like deletion)
                if (isManualUpdateRef.current) {
                    // console.log('⏭️ [POLLING] Skipping poll due to recent manual update');
                    isManualUpdateRef.current = false; // Reset flag
                    return;
                }

                const currentEditData = DPTRef.current?.getEditData?.();
                if (currentEditData && Array.isArray(currentEditData)) {
                    // Only update if data actually changed (to avoid unnecessary re-renders)
                    setDecisionPointsEditData((prev: any[]) => {
                        const prevStr = JSON.stringify(prev);
                        const currentStr = JSON.stringify(currentEditData);
                        if (prevStr !== currentStr) {
                            // console.log('🔄 [POLLING] Data changed, updating from', prev.length, 'to', currentEditData.length, 'items');
                            return currentEditData;
                        }
                        return prev;
                    });
                }
            }, 500); // Check every 500ms for changes

            return () => clearInterval(interval);
        } else if (!isDPTEditing) {
            // Reset when exiting edit mode
            setDecisionPointsEditData([]);
        }
    }, [isDPTEditing, DPTRef]);

    // Initialize edit data when entering edit mode (only when isDPTEditing changes, not when data changes)
    useEffect(() => {
        if (isDPTEditing && decisionPointsData && decisionPointsData.length > 0) {
            setDecisionPointsEditData(decisionPointsData);
        }
    }, [isDPTEditing]); // Only depend on isDPTEditing to prevent re-initialization when data changes

    // 🔥 CRITICAL: Only compute currentSaveParams when in edit mode to prevent race conditions
    // If we compute it when NOT editing, fresh API data can overwrite auto-saved edits
    const currentSaveParams = useMemo(() => {
        if (!isDPTEditing) {
            // When not editing, return empty params to prevent auto-save from triggering
            return {
                tableData: [],
                selectedMonth: selectedMonth || '',
                pendingDeletes: [],
            };
        }

        // When editing, use actual edit data
        return {
            tableData: decisionPointsEditData.length > 0 ? decisionPointsEditData : decisionPointsData,
            selectedMonth: selectedMonth || '',
            pendingDeletes: pendingDeletes,
        };
    }, [isDPTEditing, decisionPointsEditData, decisionPointsData, selectedMonth, pendingDeletes]);

    // 🔥 CRITICAL: Memoize initialParams to prevent it from changing when decisionPointsData changes
    // This ensures auto-save detection works correctly even after date changes fetch new data
    const initialParamsRef = useRef({
        tableData: [],
        selectedMonth: selectedMonth || '',
        pendingDeletes: [],
    });

    // Capture initial params when entering edit mode
    useEffect(() => {
        if (isDPTEditing && decisionPointsData && decisionPointsData.length > 0) {
            // Only capture if initialParams is empty (first time entering edit mode)
            if (initialParamsRef.current.tableData.length === 0) {
                initialParamsRef.current = {
                    tableData: JSON.parse(JSON.stringify(decisionPointsData)), // Deep clone
                    selectedMonth: selectedMonth || '',
                    pendingDeletes: [],
                };
                console.log('🎯 [DPT AUTO-SAVE] Captured initial data:', initialParamsRef.current);
            }
        } else if (!isDPTEditing) {
            // Reset initialParams when exiting edit mode (prepare for next edit session)
            initialParamsRef.current = {
                tableData: [],
                selectedMonth: selectedMonth || '',
                pendingDeletes: [],
            };
        }
    }, [isDPTEditing, decisionPointsData, selectedMonth]);

    useAutoSaveParams({
        pageKey: decisionPointsPageKey,
        initialParams: initialParamsRef.current,
        saveParamsCur: currentSaveParams,
        isBegin: isDPTEditing,
        isChangeValue: true,
    });

    // Handler: Check for auto-save before entering edit mode
    const handleDecisionPointsEdit = useCallback(() => {
        // Check if auto-save exists before entering edit mode
        if (showRestorePrompt(decisionPointsPageKey)) {
            setAutoSaveVisible(true);
        } else {
            DPTRef.current?.handleEdit();
        }
    }, [showRestorePrompt, decisionPointsPageKey, DPTRef]);

    // Handler: Discard auto-save and enter edit mode
    const handleAutoSaveCancel = useCallback(() => {
        setAutoSaveVisible(false);
        deleteSaveParamsByPageKey(decisionPointsPageKey);
        setPendingDeletes([]); // Clear pending deletes when discarding auto-save
        DPTRef.current?.handleEdit();
    }, [deleteSaveParamsByPageKey, decisionPointsPageKey, setPendingDeletes, DPTRef]);

    // Handler: Restore from auto-save
    const handleAutoSaveConfirm = useCallback(async () => {
        const saveValue = getSaveParamsValue(decisionPointsPageKey) || {};

        console.log('🔄 [AUTO-SAVE RESTORE] Restoring from auto-save:', saveValue);

        setAutoSaveVisible(false);

        // 🔥 CRITICAL: Set manual update flag FIRST to prevent polling from overwriting
        isManualUpdateRef.current = true;

        // Note: We no longer restore the selected month from auto-save
        // The month is now controlled by the parent component
        // We simply restore the data that was saved at that time

        // Restore pending deletes if they exist
        if (saveValue.pendingDeletes && Array.isArray(saveValue.pendingDeletes)) {
            console.log('🗑️ [AUTO-SAVE RESTORE] Restoring pending deletes:', saveValue.pendingDeletes.length, 'items');
            setPendingDeletes(saveValue.pendingDeletes);
        }

        // 🔥 CRITICAL: Update decisionPointsData with saved data
        // This ensures the data is available when TableContent enters edit mode
        if (saveValue.tableData && Array.isArray(saveValue.tableData) && saveValue.tableData.length > 0) {
            console.log('📦 [AUTO-SAVE RESTORE] Restoring table data:', saveValue.tableData.length, 'items');
            console.log('📦 [AUTO-SAVE RESTORE] First item sample:', {
                id: saveValue.tableData[0]?.id,
                issue: saveValue.tableData[0]?.issue,
                bos: saveValue.tableData[0]?.bos
            });
            
            // Update decisionPointsData with restored data (same pattern as Studios)
            setDecisionPointsData(saveValue.tableData);
            
            // Update edit data state for auto-save tracking
            setDecisionPointsEditData(saveValue.tableData);
        }

        // 🔥 CRITICAL: Enter edit mode AFTER state updates complete
        // We need to wait for React to finish updating decisionPointsData
        // so that TableContent receives the restored data when it enters edit mode
        // Using a longer timeout to ensure state updates are fully processed
        setTimeout(() => {
            console.log('✅ [AUTO-SAVE RESTORE] Entering edit mode now');
            // Directly update TableContent's internal tableData state to ensure it has the restored data
            // This is critical because the datas prop might not have updated yet due to React batching
            if (DPTRef.current && saveValue.tableData) {
                console.log('🔧 [AUTO-SAVE RESTORE] Directly updating TableContent tableData');
                DPTRef.current.setTableData?.(saveValue.tableData);
            }
            DPTRef.current?.handleEdit();
        }, 200);
    }, [getSaveParamsValue, decisionPointsPageKey, setPendingDeletes, setDecisionPointsData, setDecisionPointsEditData, DPTRef]);

    // Clear auto-save after successful save
    const clearDPTAutoSave = useCallback(() => {
        deleteSaveParamsByPageKey(decisionPointsPageKey);
    }, [deleteSaveParamsByPageKey, decisionPointsPageKey]);

    // Check if there are unsaved changes (for RoutePrompt)
    const hasDPTUnsavedChanges = useMemo(() => {
        if (!isDPTEditing) return false;
        
        const initialData = initialParamsRef.current?.tableData;
        if (!initialData || initialData.length === 0) return false;
        
        const currentData = decisionPointsEditData.length > 0 ? decisionPointsEditData : decisionPointsData;
        
        // Compare using JSON stringify
        const hasChanges = JSON.stringify(initialData) !== JSON.stringify(currentData);
        
        return hasChanges;
    }, [isDPTEditing, decisionPointsEditData, decisionPointsData]);

    return {
        // State
        autoSaveVisible,
        decisionPointsEditData,
        decisionPointsPageKey,
        isManualUpdateRef,
        hasDPTUnsavedChanges,

        // State setters (for external updates)
        setDecisionPointsEditData,

        // Handlers
        handleDecisionPointsEdit,
        handleAutoSaveCancel,
        handleAutoSaveConfirm,
        clearDPTAutoSave,
    };
};

